import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import {
	contentControllerGetChunkWords,
	contentControllerGetManifestForProcessingContent,
	type TimedWordDto,
} from "@/shared/api/generated"

export interface TextChunkWord extends TimedWordDto {
	chunkIdx: number
}

interface UseTextChunkWordsResult {
	/** Every loaded word, in text (= chunk) order */
	words: TextChunkWord[]
	/** Chunk indexes published audio-first whose words are still pending */
	pendingChunks: Set<number>
	/** Re-check the manifest and (re)fetch chunks without words yet */
	invalidate: () => void
}

const fetchWordsJson = async (
	url: string,
	signal: AbortSignal
): Promise<TimedWordDto[]> => {
	const response = await axios.get<TimedWordDto[]>(url, {
		signal,
		headers: { "Cache-Control": "public, max-age=31536000" },
	})
	return response.data
}

/**
 * Per-chunk word timings of a TEXT (selection) item — the char-offset words
 * the server projects onto the source text.
 *
 * Completed content: per-chunk combined JSONs (immutable, browser-cached;
 * a chunk without words has an empty slot). Processing content: the
 * processing manifest lists the chunks, `chunk-words/:idx` serves each one
 * as {pending} / {words} / {url}; call invalidate() on NEW_CHUNK /
 * CHUNK_WORDS socket events to pick up what landed since.
 *
 * Selections are short (one screen of text), so unlike the paged markdown
 * hook everything is kept in memory — no windowing/eviction.
 */
export const useTextChunkWords = (
	contentId: string,
	isProcessing: boolean,
	combinedUrls: string[]
): UseTextChunkWordsResult => {
	const [chunkMap, setChunkMap] = useState<Map<number, TimedWordDto[]>>(
		new Map()
	)
	const [pendingChunks, setPendingChunks] = useState<Set<number>>(new Set())
	const [fetchVersion, setFetchVersion] = useState(0)
	const loadedRef = useRef<Set<number>>(new Set())
	const inFlightRef = useRef<Map<number, AbortController>>(new Map())

	const storeChunk = useCallback((chunkIdx: number, words: TimedWordDto[]) => {
		loadedRef.current.add(chunkIdx)
		setChunkMap(prev => {
			const next = new Map(prev)
			next.set(chunkIdx, words)
			return next
		})
		setPendingChunks(prev => {
			if (!prev.has(chunkIdx)) return prev
			const next = new Set(prev)
			next.delete(chunkIdx)
			return next
		})
	}, [])

	const markPending = useCallback((chunkIdx: number, pending: boolean) => {
		setPendingChunks(prev => {
			if (prev.has(chunkIdx) === pending) return prev
			const next = new Set(prev)
			if (pending) next.add(chunkIdx)
			else next.delete(chunkIdx)
			return next
		})
	}, [])

	useEffect(() => {
		let cancelled = false

		const loadChunk = (chunkIdx: number, run: (signal: AbortSignal) => Promise<void>) => {
			if (loadedRef.current.has(chunkIdx) || inFlightRef.current.has(chunkIdx)) return
			const controller = new AbortController()
			inFlightRef.current.set(chunkIdx, controller)
			run(controller.signal)
				.catch(e => {
					if (axios.isCancel(e)) return
					if (e instanceof DOMException && e.name === "AbortError") return
					console.error("Failed to fetch text chunk words:", e)
				})
				.finally(() => {
					inFlightRef.current.delete(chunkIdx)
				})
		}

		if (!isProcessing) {
			combinedUrls.forEach((url, chunkIdx) => {
				if (!url) return
				loadChunk(chunkIdx, async signal => {
					const words = await fetchWordsJson(url, signal)
					if (!cancelled) storeChunk(chunkIdx, words)
				})
			})
			return
		}

		const controller = new AbortController()
		contentControllerGetManifestForProcessingContent(contentId, {
			signal: controller.signal,
		})
			.then(manifest => {
				if (cancelled) return
				for (const chunk of manifest.chunks) {
					const chunkIdx = chunk.chunkIdx
					if (chunk.wordsReady === false) {
						markPending(chunkIdx, true)
						continue
					}
					loadChunk(chunkIdx, async signal => {
						const res = await contentControllerGetChunkWords(
							contentId,
							String(chunkIdx),
							{ signal }
						)
						if (cancelled) return
						if (res.pending) {
							markPending(chunkIdx, true)
							return
						}
						if (res.words) {
							storeChunk(chunkIdx, res.words)
							return
						}
						if (res.url) {
							storeChunk(chunkIdx, await fetchWordsJson(res.url, signal))
						}
					})
				}
			})
			.catch(e => {
				if (axios.isCancel(e)) return
				console.error("Failed to fetch processing manifest for text words:", e)
			})

		return () => {
			cancelled = true
			controller.abort()
		}
	}, [contentId, isProcessing, combinedUrls, fetchVersion, storeChunk, markPending])

	// Abort everything on unmount
	useEffect(() => {
		const inFlight = inFlightRef.current
		return () => {
			for (const controller of inFlight.values()) controller.abort()
			inFlight.clear()
		}
	}, [])

	const invalidate = useCallback(() => {
		setFetchVersion(v => v + 1)
	}, [])

	const words = useMemo(() => {
		const all: TextChunkWord[] = []
		const chunkIdxs = [...chunkMap.keys()].sort((a, b) => a - b)
		for (const chunkIdx of chunkIdxs) {
			for (const word of chunkMap.get(chunkIdx) ?? []) {
				all.push({ ...word, chunkIdx })
			}
		}
		return all
	}, [chunkMap])

	return { words, pendingChunks, invalidate }
}
